/**
 * Event Logger Service
 *
 * Logs analytics events to Supabase analytics_events table.
 * Powers the /admin/dashboard analytics views (Dashboard, Testers, Activity).
 *
 * Event types:
 *   - funnel_* — Anon conversion funnel (qr_scanned → signup_completed)
 *   - manual_add — Profile user adds a contact manually
 *   - app_open — Profile user opens the app (browse tracking, 30min dedup)
 *   - connection_location — Location data backfilled after connection
 *   - error_* — Connection errors (see logConnectionError)
 *
 * Tester identity: Always use getProfileUsername() → event_data.profileUsername.
 */

import { supabase, getAuthUserId, authReady, getAccessToken } from '../supabase';
import { getProfileUserId } from '../../utils/identityUtils';
import { getExchangeVariant } from '../../utils/exchangeVariant';
import { getVersionNumber } from '../../version';

/**
 * Get the Profile user's actual name from profile_user_data.
 * This is the reliable tester identifier — the Profile's own name.
 * Returns null for Anon users or if profile data doesn't exist.
 */
export const getProfileUsername = () => {
  try {
    const data = JSON.parse(localStorage.getItem('profile_user_data'));
    return data?.name || null;
  } catch {
    return null;
  }
};

/**
 * Get current branch name
 * - Production/Netlify: from REACT_APP_BRANCH env var
 * - Local development: 'local'
 */
export const getBranch = () => {
  return process.env.REACT_APP_BRANCH || 'local';
};

/**
 * Split a raw event_type string into the analytics_events schema format.
 * Same 3-rule logic used in the SQL backfill (migration 008).
 *   funnel_* → { event_type: 'funnel', event_name: <remainder> }
 *   error_*  → { event_type: 'error',  event_name: <remainder> }
 *   else     → { event_type: 'action', event_name: <full value> }
 */
export const splitEventType = (rawType) => {
  if (rawType.startsWith('funnel_')) return { event_type: 'funnel', event_name: rawType.slice(7) };
  if (rawType.startsWith('error_')) return { event_type: 'error', event_name: rawType.slice(6) };
  return { event_type: 'action', event_name: rawType };
};

/**
 * Map analytics_events rows back to the legacy shape used by dashboard and error log.
 * Inverse of splitEventType: reconstructs the compound event_type string
 * and aliases metadata → event_data, source_branch → branch.
 */
export const mapAnalyticsEvent = (row) => ({
  ...row,
  event_type: row.event_type === 'action' ? row.event_name : `${row.event_type}_${row.event_name}`,
  event_data: row.metadata,
  branch: row.source_branch,
});

/**
 * Build the analytics_events payload.
 * Attaches profile_user_id when available; auth_user_id always present.
 * Every event's metadata carries the active exchange variant (CHAT/CARD/TEXT
 * rail, variants block plan 1) AND app_version — the client's bundled version
 * number (f/prod-key-rotation). One stamp here covers all call sites; extra
 * jsonb keys, so mapAnalyticsEvent aliasing and the dashboard are unaffected.
 * app_version powers the stale-client gate run before the legacy Supabase keys
 * are disabled (old bundles carry no stamp → counted as pre-rotation clients).
 */
const buildAnalyticsPayload = (
  eventType,
  eventData,
  { profileUserId: overrideId, withoutProfile = false } = {}
) => {
  // withoutProfile: the event goes signed out, and the server refuses a
  // signed-out event that names a profile (open-access lockdown).
  const profileUserId = withoutProfile ? null : overrideId || getProfileUserId();
  const { event_type, event_name } = splitEventType(eventType);

  return {
    auth_user_id: getAuthUserId(),
    profile_user_id: profileUserId || null,
    event_type,
    event_name,
    metadata: { ...eventData, variant: getExchangeVariant(), app_version: getVersionNumber() },
    source_branch: getBranch(),
  };
};

/**
 * Fallback: Use sendBeacon to log event (Safari-friendly)
 * Safari blocks regular fetch requests when opened from iMessage in a new tab.
 * sendBeacon is designed for analytics and has better browser support.
 */
const logEventWithBeacon = (payload) => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    console.warn('[ANALYTICS] Missing Supabase credentials for beacon');
    return false;
  }

  const url = `${supabaseUrl}/rest/v1/analytics_events`;
  const body = JSON.stringify(payload);

  // sendBeacon with JSON requires a Blob
  const blob = new Blob([body], { type: 'application/json' });

  // Note: sendBeacon doesn't support custom headers directly,
  // so we need to use a URL with the apikey as a query param
  // This is less secure but necessary for sendBeacon compatibility
  const urlWithKey = `${url}?apikey=${supabaseKey}`;

  try {
    const success = navigator.sendBeacon(urlWithKey, blob);
    return success;
  } catch (e) {
    console.warn('[ANALYTICS] sendBeacon failed:', e);
    return false;
  }
};

/**
 * Robust fallback chain for analytics logging when Supabase client fails.
 * Safari's new browser context (from iMessage/QR scan) can silently break REST calls.
 *
 * Chain: fetch+keepalive (supports auth headers) → sendBeacon (last resort)
 */
const logEventFallback = (payload) => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    console.warn('[ANALYTICS] Missing Supabase credentials for fallback');
    return;
  }

  const url = `${supabaseUrl}/rest/v1/analytics_events`;
  const body = JSON.stringify(payload);

  // The signed-in session when there is one (open-access lockdown: an event
  // naming a profile is accepted only from that profile's own session; a
  // signed-out event still goes with the public key).
  const bearer = getAccessToken() || supabaseKey;

  // Try fetch with keepalive first (supports auth headers, survives page unload)
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseKey,
        Authorization: `Bearer ${bearer}`,
        Prefer: 'return=minimal',
      },
      body,
      keepalive: true,
    }).catch(() => {
      // fetch+keepalive failed — try sendBeacon as last resort
      logEventWithBeacon(payload);
    });
  } catch {
    // Synchronous fetch error — try sendBeacon
    logEventWithBeacon(payload);
  }

  console.log(`[ANALYTICS] Fallback attempted for ${payload.event_type}:${payload.event_name}`);
};

/**
 * Log an analytics event to Supabase
 * Uses Supabase client first, falls back to fetch+keepalive then sendBeacon
 * @param {string} eventType - Type of event (e.g., 'funnel_qr_scanned')
 * @param {object} eventData - Additional event context
 * @param {{profileUserId?: string, withoutProfile?: boolean}} [options] - withoutProfile:
 *   send profile_user_id null (for events recorded while the session is gone)
 */
export const logEvent = async (eventType, eventData = {}, options = {}) => {
  // Send under the signed-in session: an event that names a profile is
  // accepted only from that profile's own session (open-access lockdown).
  let signedOut = false;
  try {
    await authReady;
  } catch {
    // sign-in failed — the event still goes, signed out, so it names no profile
    signedOut = true;
  }
  const payload = buildAnalyticsPayload(eventType, eventData, {
    ...options,
    withoutProfile: options.withoutProfile || signedOut,
  });

  try {
    const { error } = await supabase.from('analytics_events').insert(payload);

    if (error) {
      console.warn('[ANALYTICS] Insert failed:', error.message);
      logEventFallback(payload);
      return;
    }

    // Log to console in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ANALYTICS] ${eventType}:`, { ...payload });
    }
  } catch (error) {
    console.warn('[ANALYTICS] Failed to log event:', error);

    // Try fallback on ANY exception — Safari's new browser context can throw
    // various errors (TypeError, AbortError, etc.), not just 'Load failed'
    logEventFallback(payload);
  }
};

// ============================================================
// CORE FUNNEL EVENTS
// See /docs/analytics.md for event definitions
// ============================================================

/**
 * Step 1: QR Scanned
 * Logged when anon user lands after scanning QR
 * Only logs once per chatRoomId (deduped via localStorage)
 *
 * @param {string} [trigger] - how a profile-to-profile exchange started:
 *   'in_app_scan' | 'link_scan' | 'proximity' (signup-connect — the KPI
 *   counting rule needs "how it happened", which no table records)
 */
export const logFunnelQRScanned = (chatRoomId, profileUsername, trigger) => {
  const key = `analytics_qr_scanned_${chatRoomId}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');
  logEvent('funnel_qr_scanned', { chatRoomId, profileUsername, trigger });
};

/**
 * Two profile holders' offer ended without connecting (E3b-2a step 10): so a
 * swap that didn't happen is visible on the dashboard, with why.
 * @param {string|null} chatRoomId - the host's QR room (null: nearby before a match)
 * @param {{reason: string, role?: string, via?: string, stage?: string}} detail
 *   reason: left · silence · expired · closed · busy · gone · dismissed · failed
 */
export const logExchangeOfferEnded = (chatRoomId, detail) => {
  logEvent('exchange_offer_ended', { chatRoomId, ...detail });
};

// funnel_landing_shown / funnel_landing_open_chat RETIRED (tiered-exchange
// Step 8, 2026-08-08) — the chat-landing they measured is gone with the
// sans-chat pivot. The 4-step funnel below replaces them (epic § 4-step
// funnel; dashboard re-spec rides Plan 5).

/**
 * TEXT-variant micro-event: the interstitial rendered (variants block plan 2,
 * epic §3 — the honest denominator: a no-tap is a clean "no interest" signal,
 * so step-1 rates read against interstitials SHOWN, not scans). Deduped per
 * room; the variant stamp rides buildAnalyticsPayload like every event.
 */
export const logFunnelInterstitialShown = (chatRoomId) => {
  const key = `analytics_interstitial_shown_${chatRoomId}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');
  logEvent('funnel_interstitial_shown', { chatRoomId });
};

/**
 * Tiered funnel step 1: complete exchange — the Anon finished share-back OR
 * just-saved (both keep the connection; method disambiguates give-and-get vs
 * just-get). Deduped per room: one completed exchange per connection.
 */
export const logFunnelExchangeCompleted = (chatRoomId, method) => {
  const key = `analytics_exchange_completed_${chatRoomId}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');
  logEvent('funnel_exchange_completed', { chatRoomId, method });
};

/**
 * Tiered funnel step 2: got QR — the Simple-profile mint (receive → sharer;
 * the propagation seed rate reads 1→2). Once per person.
 */
export const logFunnelQRCreated = (profileUserId, sourceChatRoomId) => {
  const key = 'analytics_qr_created';
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');
  logEvent('funnel_qr_created', { profileUserId, sourceChatRoomId });
};

/**
 * Tiered funnel step 3: connected as Simple — an SP hosted a NEW exchange
 * (the hero metric: every step-3 manufactures a step-1 for the next person).
 * Repeatable across rooms — a count over time, deduped per room only.
 */
export const logFunnelConnectedAsSimple = (chatRoomId) => {
  const key = `analytics_connected_as_simple_${chatRoomId}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');
  logEvent('funnel_connected_as_simple', { chatRoomId });
};

/**
 * Branch 2b: Contact Saved
 * User saves contact without requesting link
 * @param {string} chatRoomId
 * @param {string} path - 'receive_share' or 'receive_only'
 * @param {string} anonName - Optional: for receive_only path where name wasn't collected earlier
 */
export const logFunnelContactSaved = async (chatRoomId, path, anonName = null) => {
  const eventData = { chatRoomId, path };
  if (anonName) {
    eventData.anonName = anonName;
  }
  const profileUsername = getProfileUsername();
  if (profileUsername) eventData.profileUsername = profileUsername;
  return logEvent('funnel_contact_saved', eventData);
};

/**
 * Step 3: Chat Sent
 * Profile user sends a chat message
 */
export const logFunnelChatSent = (chatRoomId) => {
  logEvent('funnel_chat_sent', { chatRoomId, profileUsername: getProfileUsername() });
};

/**
 * Connection deleted (CD #15). "Track broadly, display narrowly; log only what
 * the DB doesn't already record, tagged with where it happened" (growth.md §47).
 * The deletion itself is already queryable from connections.*_left_at — what the
 * DB does NOT record is WHERE the user deleted from, so this carries only a
 * `source` tag plus chatRoomId for attribution. No contact data.
 * @param {string} source - 'people' (People-list swipe) or 'chats' (Chats-list
 *   swipe) — the two delete affordances today; delete is not offered on Detail.
 * @param {string} chatRoomId
 */
export const logConnectionDeleted = (source, chatRoomId) => {
  logEvent('connection_deleted', { source, chatRoomId });
};

/**
 * Step 4: Signup Completed (Waitlist Signup)
 * User signs up to be notified when app launches
 * Only logs once per chatRoomId (deduped via localStorage)
 * Requires qr_scanned to have been logged first (prevents orphan events)
 * — Unless skipQrGuard is true (Safari new browser context loses the qr_scanned flag)
 * @param {string} chatRoomId
 * @param {string} source - 'connections_list' or 'connection_page' (WelcomePage uses logEvent directly with 'welcome'/'post_clear')
 * @param {boolean} sessionActive - true if signed up during exchange session
 * @param {object} contactInfo - Optional contact info for waitlist (never includes
 *   the contact value itself — see buildSignupWaitlistEventData)
 * @param {string} contactInfo.contactType - 'email' or 'phone'
 * @param {string} contactInfo.anonName - User's name
 * @param {string} contactInfo.connectedWith - Profile user's name they connected with
 * @param {string|null} contactInfo.location - Neighborhood/venue string (null if unavailable)
 * @param {object} options - Optional settings
 * @param {boolean} options.skipQrGuard - If true, skip qr_scanned guard (Safari new browser context has no localStorage)
 */
/**
 * Pure event-data builder for funnel_signup_waitlist — separated from the
 * dedup/network write so the shape is unit-testable (the buildWaitlistRow
 * idiom in waitlist.js). contactValue is deliberately never copied:
 * analytics_events is client-readable, and the contact itself lives in the
 * RLS-locked launch_waitlist table (f/db-waitlist-select-fix). WelcomePage's
 * inline event follows the same rule.
 */
export const buildSignupWaitlistEventData = (
  chatRoomId,
  source,
  sessionActive,
  contactInfo = null
) => {
  const eventData = { chatRoomId, source, sessionActive };
  if (contactInfo) {
    eventData.contactType = contactInfo.contactType;
    eventData.anonName = contactInfo.anonName;
    eventData.connectedWith = contactInfo.connectedWith;
    eventData.location = contactInfo.location;
  }
  return eventData;
};

/**
 * Waitlist Signup (funnel_signup_waitlist)
 * Anon signed up for the waitlist via LaunchSignupOverlay.
 * One per Anon per chatRoomId (deduped via localStorage).
 */
export const logFunnelSignupWaitlist = (
  chatRoomId,
  source,
  sessionActive,
  contactInfo = null,
  options = {}
) => {
  const { skipQrGuard = false } = options;

  // Don't log if qr_scanned was never logged (prevents orphan events)
  // When skipQrGuard is true, skip this check — Safari opens invite/signup links in a new
  // browser context without localStorage from the original session
  if (!skipQrGuard) {
    const qrKey = `analytics_qr_scanned_${chatRoomId}`;
    if (!localStorage.getItem(qrKey)) {
      console.log(
        '[ANALYTICS] Skipping funnel_signup_waitlist - no qr_scanned event for this chatRoomId'
      );
      return;
    }
  }

  const key = `analytics_signup_waitlist_${chatRoomId}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');

  logEvent(
    'funnel_signup_waitlist',
    buildSignupWaitlistEventData(chatRoomId, source, sessionActive, contactInfo)
  );
};

/**
 * Profile Shared
 * Profile user shares their contact information
 * @param {string} chatRoomId
 * @param {string[]} contactTypes - Array of contact types shared: ['phone', 'email', 'instagram', etc.]
 * @param {string} nameSharing - 'first' or 'full'
 * @param {string} [shareMethod] - Trigger path: 'last_share' | 'phone' (quick-share row
 *   one-taps) | 'edit' (row's Edit overlay) | 'overlay' (other overlay openers) |
 *   'detail_page' (inline SHARED list) — wedge-depth evidence for the strategy checkpoint
 */
export const logFunnelProfileShared = (chatRoomId, contactTypes, nameSharing, shareMethod) => {
  logEvent('funnel_profile_shared', {
    chatRoomId,
    contactTypes,
    nameSharing,
    ...(shareMethod ? { shareMethod } : {}),
    profileUsername: getProfileUsername(),
  });
};

/**
 * Invite Sent
 * Profile user sends an invite CTA to Anon user
 * No dedup needed — invite button disappears after send
 * @param {string} chatRoomId
 */
export const logFunnelInviteSent = (chatRoomId) => {
  logEvent('funnel_invite_sent', { chatRoomId, profileUsername: getProfileUsername() });
};

/**
 * Invite Clicked
 * Anon user clicks the invite CTA card in chat
 * @param {string} chatRoomId
 * @param {boolean} canCreateProfile - Whether Anon was routed to profile creation or waitlist
 */
export const logFunnelInviteClicked = (chatRoomId, canCreateProfile) => {
  logEvent('funnel_invite_clicked', { chatRoomId, canCreateProfile });
};

/**
 * Moment entry tapped (D3 Moments)
 * Fires on EVERY tap of the camera/moment-entry affordance — the reach signal,
 * whether or not a moment is ultimately sent. Surface only, NO content (capture
 * rule: location, not payload). v1 reads this to judge usage before the
 * text-authoring increment.
 * @param {string} chatRoomId
 * @param {string} surface - where it was tapped (e.g. 'exchange_chat', 'host_flow')
 */
export const logMomentEntryTapped = (chatRoomId, surface) => {
  logEvent('moment_entry_tapped', { chatRoomId, surface });
};

/**
 * Moment sent (D3 Moments)
 * A photo-moment was created from an in-exchange capture. No content.
 * @param {string} chatRoomId
 * @param {string} surface
 */
export const logMomentSent = (chatRoomId, surface) => {
  logEvent('moment_sent', { chatRoomId, surface });
};

/**
 * Repeat connect (signup-meetings § Measure it): two people who already know each
 * other connected again — a reconnect (same meeting) or a re-meet (a new one).
 * Logged once per connect, by the deciding phone (the host). No location content.
 * @param {string} chatRoomId - the pair's existing room
 * @param {Object} p
 * @param {'reconnect'|'remeet'} p.kind
 * @param {number|null} p.minutesSinceMeetingStart - since the meeting being compared
 * @param {string} p.exchangeType - 'pxp' | 'anon_profile'
 * @param {string|null} p.trigger - in_app_scan | link_scan | proximity (PXP only)
 */
export const logMeetingRepeatConnect = (
  chatRoomId,
  { kind, minutesSinceMeetingStart, exchangeType, trigger = null }
) => {
  logEvent('meeting_repeat_connect', {
    chatRoomId,
    kind,
    minutes_since_meeting_start: minutesSinceMeetingStart,
    exchange_type: exchangeType,
    trigger,
  });
};

// ============================================================
// PENDING PROFILE CONVERSION EVENTS
// ============================================================

/**
 * Pending Profile Created
 * Logged when Anon confirms phone number and pending record is created.
 * Fires from browser (possibly Safari ephemeral context) — logEvent handles fallback chain.
 *
 * @param {string} source - 'anon_conversion' or 'demo_gate'
 * @param {string} phone - Last 4 digits only (privacy)
 * @param {boolean} hasAnonContext - Whether anon_user_id is present
 */
/**
 * Phone Confirmed for PWA (funnel_signup_confirmed)
 * Anon confirmed their phone for PWA conversion via PendingProfileOverlay.
 * One per Anon (deduped via localStorage).
 */
export const logFunnelSignupConfirmed = (source, phone, hasAnonContext, chatRoomId) => {
  const key = 'analytics_signup_confirmed';
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');

  const phoneLast4 = phone ? phone.slice(-4) : '';
  logEvent('funnel_signup_confirmed', {
    source,
    phoneLast4,
    has_anon_context: hasAnonContext,
    chatRoomId: chatRoomId || null,
  });
};

/**
 * PWA Conversion Complete (funnel_signup_converted)
 * Logged when convert_pending_profile RPC succeeds — the user has a real profile.
 * Deduplicates per profile_user_id (not per chatRoomId).
 *
 * @param {string} profileUserId
 * @param {string|null} anonName - extracted from chat messages
 * @param {number} connectionsMigrated
 * @param {string|null} connectedWith - most recent Profile user's name
 * @param {string|null} chatRoomId - from pending record (triggering connection)
 * @param {number} anonIdsMigrated - how many anon ids conversion v3 claimed (multi-id healing)
 */
export const logFunnelSignupConverted = (
  profileUserId,
  anonName,
  connectionsMigrated,
  connectedWith,
  chatRoomId,
  anonIdsMigrated
) => {
  const key = `analytics_signup_converted_${profileUserId}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, 'true');

  logEvent(
    'funnel_signup_converted',
    {
      profile_user_id: profileUserId,
      connections_migrated: connectionsMigrated,
      anon_ids_migrated: anonIdsMigrated || 0,
      anonName: anonName || null,
      connectedWith: connectedWith || null,
      chatRoomId: chatRoomId || null,
    },
    { profileUserId }
  );
};

// ============================================================
// ACTION EVENTS (non-funnel)
// ============================================================

/**
 * Manual Add
 * Profile user manually saves a connection (name save)
 * @param {string} chatRoomId
 * @param {string} contactName - Name entered by user
 * @param {boolean} hasNote - Whether a note was added
 * @param {string} category - 'Social' | 'Work' | 'Family' | 'Other'
 */
export const logManualAdd = (chatRoomId, contactName, hasNote, category) => {
  logEvent('manual_add', {
    chatRoomId,
    contactName,
    hasNote,
    category,
    profileUsername: getProfileUsername(),
  });
};

// ============================================================
// ENGAGEMENT EVENTS
// ============================================================

/**
 * Update last_active_at on the profiles table.
 * Called alongside logAppOpen on People list mount.
 * Separate 5-min dedup (vs 30-min analytics dedup) — activity display needs fresher data.
 * Fire-and-forget: does not block UI.
 */
export const updateLastActive = () => {
  const FIVE_MINUTES = 5 * 60 * 1000;
  const key = 'last_active_at_updated';
  const last = localStorage.getItem(key);

  if (last && Date.now() - parseInt(last, 10) < FIVE_MINUTES) {
    return; // Too recent, skip
  }

  const profileUserId = getProfileUserId();
  if (!profileUserId) return; // Anon users don't have profiles

  localStorage.setItem(key, String(Date.now()));
  supabase
    .from('profiles')
    .update({ last_active_at: new Date().toISOString() })
    .eq('profile_user_id', profileUserId)
    .then(({ error }) => {
      if (error) console.warn('[ACTIVITY] Failed to update last_active_at:', error.message);
    });
};

/**
 * App Open — Profile user opened the app (browse tracking)
 * Dedup: only log if >30 min since last open (localStorage key)
 * Powers "Browsed" column in Testers view and Browse rows in Activity feed
 */
export const logAppOpen = () => {
  const THIRTY_MINUTES = 30 * 60 * 1000;
  const key = 'analytics_last_app_open';
  const last = localStorage.getItem(key);

  if (last && Date.now() - parseInt(last, 10) < THIRTY_MINUTES) {
    return; // Too recent, skip
  }

  localStorage.setItem(key, String(Date.now()));
  logEvent('app_open', { profileUsername: getProfileUsername() });
};

/**
 * Posts Viewed — Profile user opened the Posts tab (feature-usage evidence
 * for the strategy checkpoint). Same 30-min dedup window as logAppOpen.
 * No chatRoomId → table query only, not on the dashboard (analytics.md).
 */
export const logPostsViewed = () => {
  const THIRTY_MINUTES = 30 * 60 * 1000;
  const key = 'analytics_last_posts_viewed';
  const last = localStorage.getItem(key);

  if (last && Date.now() - parseInt(last, 10) < THIRTY_MINUTES) {
    return; // Too recent, skip
  }

  localStorage.setItem(key, String(Date.now()));
  logEvent('posts_viewed');
};

/**
 * Connection Location — backfill location data after connection
 * Powers location display in Activity feed rows
 * @param {string} chatRoomId
 * @param {object} locationData - Location data from getLocationWithVenue()
 * @param {string} source - How location was resolved: 'radar' | 'manual' | 'quick_add'
 */
export const logConnectionLocation = (chatRoomId, locationData, source) => {
  const venueInfo = locationData?.venueInfo;
  const coords = locationData?.coords;
  logEvent('connection_location', {
    chatRoomId,
    profileUsername: getProfileUsername(),
    venueName: venueInfo?.venue?.name || null,
    city: venueInfo?.address?.cityAbbr || venueInfo?.address?.city || null,
    neighborhood: venueInfo?.address?.neighborhood || null,
    source: source || 'radar',
    gpsTier: coords?._gpsTier || null,
    gpsAccuracy: coords?._gpsAccuracy || null,
    gpsElapsed: coords?._gpsElapsed || null,
  });
};

// ============================================================
// ERROR LOGGING
// Captures hard-to-reproduce errors for debugging
// ============================================================

/**
 * Deduplication for error logging
 * Prevents the same error from being logged multiple times within a short window
 */
const recentErrors = new Map();
const DEDUP_WINDOW_MS = 60000; // 60 seconds

/**
 * Log a connection error (e.g., UserRedirect failures)
 * Includes deduplication to prevent spam from the same error type + chatRoomId
 * @param {string} errorType - Type of error: 'user_redirect', 'websocket', 'supabase', etc.
 * @param {object} errorData - Error details
 * @param {string} errorData.message - Error message
 * @param {string} errorData.code - Error code (if available)
 * @param {string} errorData.profileUserId - Profile user ID (if available)
 * @param {number} errorData.attempt - Which attempt failed (for retries)
 * @param {string} errorData.userAgent - Browser info for debugging
 */
// Known bot patterns to filter out from error logging
const BOT_PATTERNS = [
  'Google-Read-Aloud',
  'Googlebot',
  'bingbot',
  'facebookexternalhit',
  'Twitterbot',
  'LinkedInBot',
  'WhatsApp',
  'Slackbot',
  'TelegramBot',
  'Discordbot',
  'PetalBot',
  'AhrefsBot',
];

const isBot = () => {
  const ua = navigator.userAgent;
  return BOT_PATTERNS.some((pattern) => ua.includes(pattern));
};

export const logConnectionError = (errorType, errorData = {}) => {
  // Skip logging for bots - they trigger false errors when crawling links
  if (isBot()) {
    console.log(`[ERROR_LOG] Skipping bot error: ${errorType}`);
    return;
  }

  // Deduplication: Skip if same error type + chatRoomId logged within 60 seconds
  const dedupKey = `${errorType}_${errorData.chatRoomId || 'unknown'}`;
  const now = Date.now();
  const lastLogged = recentErrors.get(dedupKey);

  if (lastLogged && now - lastLogged < DEDUP_WINDOW_MS) {
    console.log(`[ERROR_LOG] Skipping duplicate error: ${dedupKey}`);
    return;
  }

  // Update last logged time
  recentErrors.set(dedupKey, now);

  // Clean up old entries to prevent memory leak
  for (const [key, timestamp] of recentErrors) {
    if (now - timestamp > DEDUP_WINDOW_MS) {
      recentErrors.delete(key);
    }
  }

  // Structure data with profile info first for easy identification
  // "Who had the problem?" should be the first thing you see
  const { profileUsername, profileUserId, ...restData } = errorData;
  const enrichedData = {
    profileUsername: profileUsername || profileUserId || 'Unknown',
    ...restData,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    url: window.location.href,
  };

  logEvent(`error_${errorType}`, enrichedData);
  console.log(`[ERROR_LOG] ${errorType}:`, enrichedData);
};

// ============================================================
// DUAL-WRITE & RECOVERY EVENTS
// Tracks Supabase sync failures and data recovery lifecycle
// ============================================================

/**
 * Dual-write Supabase upsert failed
 * @param {string} table - 'connection_data' or 'profiles'
 * @param {string} chatRoomId - Connection identifier (if applicable)
 * @param {string} error - Error message
 */
export const logDualWriteError = (table, chatRoomId, error) => {
  logEvent('error_dual_write', { table, chatRoomId, error });
};

/**
 * Data recovery triggered on app init
 * @param {'total'|'partial'} type - Total loss or partial loss
 * @param {number} missingKeys - Number of missing localStorage keys
 */
export const logRecoveryTriggered = (type, missingKeys) => {
  logEvent('recovery_triggered', { type, missingKeys, profileUsername: getProfileUsername() });
};

/**
 * Data recovery completed successfully
 * @param {number} connectionsRestored - Number of connections restored
 * @param {number} messagesRestored - Number of message threads restored
 */
export const logRecoveryCompleted = (connectionsRestored, messagesRestored) => {
  logEvent('recovery_completed', {
    connectionsRestored,
    messagesRestored,
    profileUsername: getProfileUsername(),
  });
};

/**
 * Data recovery failed
 * @param {string} step - Which recovery step failed
 * @param {string} error - Error message
 */
export const logRecoveryError = (step, error) => {
  logEvent('error_recovery', { step, error, profileUsername: getProfileUsername() });
};
